import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { LazyImage } from 'common/react/components/LazyImage';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { selectCardHandler } from './util';
import { MediaThumbnailFooter } from './MediaThumbnailFooter';
import { SvgIcon } from '../SvgIcon';

/**
 * Renders the MediaThumbnail for a news article.
 *
 * @param {string} title - The widget title.
 * @param {object} article - The club news article data.
 * @returns {JSX.Element} Component.
 */

const BASE_URL_PATH = 'news';

export const MediaThumbnailArticle = ({ article, index }) => {
    const publishLabel =
        PULSE.app.common.content.getPublishStringFromTag(article);

    const contentImage =
        PULSE.app.common.content.getImageFromContentItem(article);

    // Responsive
    const windowSize = useWindowSize();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.phablet;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
    }, [windowSize.width]);

    return (
        <article
            className={`media-thumbnail media-thumbnail--row-card-phablet ${
                publishLabel?.label?.length
                    ? `media-thumbnail--has-publish-label media-thumbnail--${publishLabel.cssClass}`
                    : ''
            }`}
        >
            <div className={`media-thumbnail__link`}>
                <a
                    className={`media-thumbnail__absolute-link js-media-thumbnail__link`}
                    href={`/${BASE_URL_PATH}/${article?.id}/${article?.titleUrlSegment}`}
                    title={article?.title}
                    aria-label={article?.title}
                    data-index-position={index + 1}
                    onClick={(evt) => selectCardHandler(evt, article?.title)}
                ></a>
                <header
                    className={`media-thumbnail__image-container placeholder placeholder--small placeholder--dark`}
                >
                    <LazyImage
                        image={contentImage}
                        mediaQueryConfig={
                            PULSE.app.common.CONSTANTS.VARIANT_CONFIGURATIONS
                                .THUMBNAIL_MEDIA
                        }
                        cssClass="js-faded-image fade-in-on-load object-fit-cover-picture__img is-loaded"
                    />

                    {publishLabel?.label?.length > 0 && (
                        <div
                            className={`media-thumbnail__content-label-container media-thumbnail__content-label-container--header ${
                                publishLabel.cssClass || ''
                            }`}
                        >
                            <span className="media-thumbnail__content-label">
                                {publishLabel.label}
                            </span>
                            <SvgIcon
                                icon={`content-label-edge`}
                                className={`icon media-thumbnail__content-label-container-edge`}
                            />
                        </div>
                    )}
                </header>
                <div className={`media-thumbnail__caption`}>
                    {publishLabel?.label?.length > 0 && (
                        <div className="media-thumbnail__content-label-container media-thumbnail__content-label-container--caption">
                            <span className="media-thumbnail__content-label">
                                {publishLabel.label}
                            </span>
                            <SvgIcon
                                icon={`content-label-edge`}
                                className={`icon media-thumbnail__content-label-container-edge`}
                            />
                        </div>
                    )}
                    <h2 className={`media-thumbnail__title`}>
                        {article?.title}
                    </h2>
                    <p className={`media-thumbnail__description`}>
                        {article?.description}
                    </p>
                    {!showResponsiveItems ? (
                        <MediaThumbnailFooter data={article} />
                    ) : null}
                </div>
            </div>
            {showResponsiveItems ? (
                <MediaThumbnailFooter data={article} />
            ) : null}
        </article>
    );
};

MediaThumbnailArticle.propTypes = {
    article: PropTypes.object,
    index: PropTypes.number
};
